"use client";

import { useEffect } from "react";
import { fraunces, inter } from "./fonts";
// global-error REPLACES app/layout.tsx when it fires, so none of that layout's
// assets — including this stylesheet — ship with it. Without this import the
// page renders completely unstyled on the app's default white ground.
import "./globals.css";

// app/layout.tsx puts these two `.variable` classNames on <html> so globals.css's
// `--font-fraunces` / `--font-inter` tokens resolve. This file renders its own
// <html>, so it imports both faces from `./fonts` and hangs them there too —
// otherwise the heading silently falls back to a generic serif while the rest
// of the app looks right. Mirror the layout exactly: `.variable`, not `.className`.

/**
 * Root-layout error boundary. Fires when app/layout.tsx itself throws, or when a
 * throw elsewhere has nowhere else to land. Styled from vantage's own tokens
 * (--ink ground, --dawn accent, Fraunces heading) and kept visually consistent
 * with app/(app)/error.tsx, the group-level boundary one level down.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    void (async () => {
      try {
        const { reportError } = await import("@/lib/flare");
        await reportError(error, { kind: "client", digest: error.digest });
      } catch {
        // The page is already broken; a failed report must not make it worse.
      }
    })();
  }, [error]);

  return (
    <html lang="en" className={`${fraunces.variable} ${inter.variable}`}>
      <head>
        {/* Next's global-error path bypasses metadata/viewport resolution entirely
            (its own DefaultGlobalError has the same gap), so without this an
            installed iOS PWA renders this page at desktop width — exactly the
            scenario this file exists for. */}
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </head>
      <body>
        <main className="welcome" style={{ minHeight: "100dvh" }}>
          <h1>Vantage didn&rsquo;t load</h1>
          <p className="welcome-body">
            Something broke before the page could start. Trying again usually fixes it, and
            nothing in your archive is affected.
          </p>
          <div className="feedback-actions" style={{ justifyContent: "center", marginTop: 8 }}>
            <button type="button" className="btn go btn-chip" onClick={() => reset()}>
              Reload Vantage
            </button>
          </div>
        </main>
      </body>
    </html>
  );
}
